import React from 'react';

import { css } from '@compiled/react';
import { UniformSlot, UniformSlotWrapperComponentProps } from '@uniformdev/canvas-react';

import { media } from '@atlaskit/primitives';

import {
	WCL_GRID_MAX_WIDTH,
	WCL_GRID_PADDING_LG,
} from '@/libraries/design/foundations/grid/src/base';
import { SECTION_DIVIDER_HEIGHTS, type SectionDividerType } from '@/libraries/design/utils';

export type UniformSplitColorBackgroundContainerProps = {
	topColor: string;
	bottomColor: string;
	sectionDivider?: SectionDividerType;
	extendBackgroundColor?: boolean;
};

const UniformSplitColorBackgroundContainer: React.FC<
	UniformSplitColorBackgroundContainerProps
	// eslint-disable-next-line complexity
> = ({ topColor, bottomColor, sectionDivider, extendBackgroundColor }) => {
	const baseStyle = css({
		position: 'relative',
		'--section-divider-height': `${SECTION_DIVIDER_HEIGHTS.mobile}px`,
		[media.above.sm]: {
			'--section-divider-height': `${SECTION_DIVIDER_HEIGHTS.tablet}px`,
		},
		[media.above.md]: {
			'--section-divider-height': `${SECTION_DIVIDER_HEIGHTS.desktop}px`,
		},
	});

	const minHeightStyle = css({
		minHeight: 'var(--section-divider-height)',
	});

	const horizontalStyle = css({
		background: `linear-gradient(to bottom, ${topColor} 0%, ${topColor} 50%, ${bottomColor} 50%, ${bottomColor} 100%)`,
	});

	const highLeftStyle = css({
		'::before': {
			clipPath: 'polygon(0 -0.25%, 0 100.25%, 100% 100.25%)',
		},
	});

	const highRightStyle = css({
		'::before': {
			clipPath: 'polygon(100% -0.25%, 0 100.25%, 100% 100.25%)',
		},
	});

	const polygonStyle = css({
		'::before': {
			clipPath: 'polygon(0 -0.25%, 70% 16%, 70% 66%, 100% 46%, 100% 100.25%, 0 100.25%)',
		},
	});

	const coverStyles = css({
		'::before': {
			content: '',
			display: 'block',
			width: '100%',
			position: 'absolute',
			insetBlockStart: 0,
			backgroundColor: `${topColor}`,
			height: 'calc((100% - var(--section-divider-height)) / 2)',
		},
		'::after': {
			content: '',
			display: 'block',
			width: '100%',
			position: 'absolute',
			zIndex: -1,
			insetBlockEnd: 0,
			backgroundColor: `${bottomColor}`,
			height: 'calc((100% - var(--section-divider-height)) / 2)',
		},
	});

	const sectionDividerComponent = (
		<div
			css={[
				css({
					position: 'absolute',
					height: 'var(--section-divider-height)',
					width: '100%',
					background: `${topColor}`,
					insetBlockStart: '50%',
					transform: 'translateY(-50%)',
					'::before': {
						content: '',
						display: 'block',
						height: '100%',
						width: '100%',
						position: 'absolute',
						insetBlockStart: 0,
						backgroundColor: `${bottomColor}`,
					},
				}),
				bottomColor === 'transparent' &&
					sectionDivider === 'high-left' &&
					css({
						clipPath: 'polygon(0 -0.25%, 100% -0.25%, 100% 100.25%)',
					}),
				bottomColor === 'transparent' &&
					sectionDivider === 'high-right' &&
					css({
						clipPath: 'polygon(0 -0.25%,  0 100.25%, 100% -0.25%)',
					}),
				bottomColor === 'transparent' &&
					sectionDivider === 'polygon' &&
					css({
						clipPath: 'polygon(0 -0.25%, 70% 16%, 70% 66%, 100% 46%, 100% -0.25%)',
					}),
				sectionDivider === 'high-left' && highLeftStyle,
				sectionDivider === 'high-right' && highRightStyle,
				sectionDivider === 'polygon' && polygonStyle,
			]}
		/>
	);

	const extendedBackgroundColorComponent = (
		<div
			css={[
				css({
					position: 'absolute',
					width: '100vw',
					height: '100%',
					[media.above.sm]: {
						width: `calc(${WCL_GRID_MAX_WIDTH}px + (${WCL_GRID_PADDING_LG}px * 2))`, // for when the grid width is stopped at its max with
						maxWidth: `min(calc(100% - ${WCL_GRID_PADDING_LG}px), calc(${WCL_GRID_MAX_WIDTH}px + 80px))`, // for when the grid width is less than its max width, and to have background color max width at grid max width + 80px
					},
					insetInlineStart: '50%',
					transform: 'translateX(-50%)',
				}),
				sectionDivider && coverStyles,
				!sectionDivider && horizontalStyle,
			]}
		>
			{sectionDivider && sectionDividerComponent}
		</div>
	);

	const slotWrapper = ({ items }: UniformSlotWrapperComponentProps) => {
		return <div css={css({ position: 'relative' })}>{items}</div>;
	};

	return (
		<div
			css={[
				baseStyle,
				sectionDivider && minHeightStyle,
				!extendBackgroundColor && sectionDivider && coverStyles,
				!extendBackgroundColor && !sectionDivider && horizontalStyle,
			]}
		>
			{sectionDivider && !extendBackgroundColor && sectionDividerComponent}
			{extendBackgroundColor && extendedBackgroundColorComponent}
			<UniformSlot name="component" wrapperComponent={sectionDivider ? slotWrapper : undefined} />
		</div>
	);
};

export default UniformSplitColorBackgroundContainer;
